import {
  CreationPolicy,
  Policy,
  PolicyGql,
  GovernanceEntityWcg,
} from '@agilelab/plugin-wb-governance-common';

import {
  parseInfrastructureTemplateIdFromPreprocessing,
  parseUseCaseTemplateIdFromPreprocessing,
} from './utils';
import { CustomAlertApi } from '@agilelab/plugin-wb-platform';

export const toCreationPolicy = ({
  governanceEntityWcg,
  alertApi,
  isClone = false,
}: {
  governanceEntityWcg: GovernanceEntityWcg;
  alertApi: CustomAlertApi;
  isClone?: boolean;
}): CreationPolicy => {
  let parsedMetadata = {};

  try {
    parsedMetadata = JSON.parse(governanceEntityWcg.additionalMetadata);
  } catch (error) {
    alertApi.post({
      message: `Policy "${governanceEntityWcg.name}" has malformed metadata. Check it with the Platform Team. Policy ID: "${governanceEntityWcg.id}".`,
      severity: 'error',
    });
    parsedMetadata = {};
  }

  return {
    id: governanceEntityWcg.id,
    groupId: governanceEntityWcg.groupId,
    status: governanceEntityWcg.status,
    name: governanceEntityWcg.name + (isClone ? ' - Copy' : ''),
    description: governanceEntityWcg.description ?? '',
    context: governanceEntityWcg.context,
    severity: governanceEntityWcg.severity,
    strategy: governanceEntityWcg.strategy,
    engine: governanceEntityWcg.engine,
    resourceType: [governanceEntityWcg.resourceType],
    version: governanceEntityWcg.version,
    selectors: governanceEntityWcg.selectors ?? [],
    tags: governanceEntityWcg.tags,
    cueSpecific: {
      cueScript: governanceEntityWcg.cueScript ?? '',
    },
    externalSpecific: {
      externalUrl: governanceEntityWcg.externalUrl ?? '',
    },
    governanceAgentSpec: {
      url: governanceEntityWcg.governanceAgentSpec?.url ?? '',
      prompt: governanceEntityWcg.governanceAgentSpec?.prompt ?? '',
    },
    timing: governanceEntityWcg.timing,
    updateTime: new Date(governanceEntityWcg.updateTime),
    policyId: governanceEntityWcg.id,
    cronExpression: governanceEntityWcg.cronExpression ?? undefined,
    interactionType: governanceEntityWcg.interactionType,
    environments: [governanceEntityWcg.environment],
    preprocessing: governanceEntityWcg.preprocessing ?? undefined,
    infrastructureTemplateId: parseInfrastructureTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    useCaseTemplateId: parseUseCaseTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    additionalMetadata: parsedMetadata,
    trigger: governanceEntityWcg.trigger,
  };
};

export const toGovernancePolicy = (gqlPolicy: PolicyGql): Policy => {
  let parsedMetadata = {};

  try {
    parsedMetadata = JSON.parse(gqlPolicy.additional_metadata);
  } catch (error) {
    parsedMetadata = {};
  }

  return {
    id: String(gqlPolicy.id),
    groupId: gqlPolicy.group_id,
    status: gqlPolicy.status,
    name: gqlPolicy.name,
    description: gqlPolicy.description || '',
    context: gqlPolicy.context,
    severity: gqlPolicy.content.severity,
    strategy: gqlPolicy.strategy,
    engine: gqlPolicy.engine,
    resourceType: gqlPolicy.resource_type.name,
    version: gqlPolicy.version,
    selectors: gqlPolicy.selector?.map(s => ({ path: s.jsonPath, ...s })) || [],
    tags: gqlPolicy.governance_entity_tags.map(t => t.tag.name),
    cueSpecific: {
      cueScript: gqlPolicy.content.cueScript || '',
    },
    externalSpecific: {
      externalUrl: gqlPolicy.content.externalUrl || '',
    },
    governanceAgentSpec: {
      url: gqlPolicy.content.governanceAgentSpec?.url || '',
      prompt: gqlPolicy.content.governanceAgentSpec?.prompt || '',
    },
    timing: gqlPolicy.timing,
    updateTime: new Date(gqlPolicy.update_time),
    policyId: gqlPolicy.governance_entity_id,
    cronExpression: gqlPolicy.cron_expression || undefined,
    interactionType: gqlPolicy.interaction_type,
    environments:
      gqlPolicy.governance_entity_environments?.map(e => e.environment.name) ||
      [],
    preprocessing: gqlPolicy.preprocessing || undefined,
    infrastructureTemplateId: parseInfrastructureTemplateIdFromPreprocessing(
      gqlPolicy.preprocessing,
    ),
    useCaseTemplateId: parseUseCaseTemplateIdFromPreprocessing(
      gqlPolicy.preprocessing,
    ),
    additionalMetadata: parsedMetadata,
    trigger: gqlPolicy.trigger,
    managedPolicyType: (parsedMetadata as any)?.managedPolicyType ?? null,
    restrictedOperations: Array.isArray(
      (parsedMetadata as any)?.restrictedOperations,
    )
      ? (parsedMetadata as any).restrictedOperations
      : [],
  };
};

export const toGovernancePolicyFromWcg = (
  governanceEntityWcg: GovernanceEntityWcg,
  alertApi: CustomAlertApi,
): Policy => {
  let parsedMetadata = {};

  try {
    parsedMetadata = JSON.parse(governanceEntityWcg.additionalMetadata);
  } catch (error) {
    alertApi.post({
      message: `Policy "${governanceEntityWcg.name}" has malformed metadata. Check it with the Platform Team. Policy ID: "${governanceEntityWcg.id}".`,
      severity: 'error',
    });
    parsedMetadata = {};
  }

  return {
    id: governanceEntityWcg.id,
    groupId: governanceEntityWcg.groupId,
    status: governanceEntityWcg.status,
    name: governanceEntityWcg.name,
    description: governanceEntityWcg.description ?? '',
    context: governanceEntityWcg.context,
    severity: governanceEntityWcg.severity,
    strategy: governanceEntityWcg.strategy,
    engine: governanceEntityWcg.engine,
    resourceType: governanceEntityWcg.resourceType,
    version: governanceEntityWcg.version,
    selectors: governanceEntityWcg.selectors ?? [],
    tags: governanceEntityWcg.tags,
    cueSpecific: {
      cueScript: governanceEntityWcg.cueScript ?? '',
    },
    externalSpecific: {
      externalUrl: governanceEntityWcg.externalUrl ?? '',
    },
    governanceAgentSpec: {
      url: governanceEntityWcg.governanceAgentSpec?.url ?? '',
      prompt: governanceEntityWcg.governanceAgentSpec?.prompt ?? '',
    },
    timing: governanceEntityWcg.timing,
    updateTime: new Date(governanceEntityWcg.updateTime),
    policyId: governanceEntityWcg.id,
    cronExpression: governanceEntityWcg.cronExpression ?? undefined,
    interactionType: governanceEntityWcg.interactionType,
    environments: [governanceEntityWcg.environment],
    preprocessing: governanceEntityWcg.preprocessing ?? undefined,
    infrastructureTemplateId: parseInfrastructureTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    useCaseTemplateId: parseUseCaseTemplateIdFromPreprocessing(
      governanceEntityWcg.preprocessing,
    ),
    additionalMetadata: parsedMetadata,
    trigger: governanceEntityWcg.trigger,
    managedPolicyType: (parsedMetadata as any)?.managedPolicyType ?? null,
    restrictedOperations: Array.isArray(
      (parsedMetadata as any)?.restrictedOperations,
    )
      ? (parsedMetadata as any).restrictedOperations
      : [],
  };
};
